/**
 * Sections can be gathered into a group: Budget, Transactions, Accounts, Ledger and Summary are all
 * Money. A group takes one place in the phone's bottom bar, and its sections are a strip of tabs at
 * the top of the page instead; the desktop sidebar lists them under the group's name.
 *
 * A group lists its sections' slugs, and a section carries its group as `group` (groupOfSlug).
 * AppRouter adds `displayed-group-<group>` to the app's container, for the room its strip takes. The
 * sections keep their own urls (/dashboard/budget, not /dashboard/money/budget), so links made
 * before the groups still work.
 */
export const sectionGroups = {
	money: { label: 'Money', icon: 'account_balance_wallet', slugs: ['budget', 'transactions', 'accounts', 'ledger', 'summary'] },
};

/** The group the section of `slug` belongs to, or null. An old slug counts as the section it became. */
export function groupOfSlug(slug) {
	const current = currentSlug(slug);
	return Object.keys(sectionGroups).find((group) => sectionGroups[group].slugs.includes(current)) || null;
}

/** Old section urls, and the section each now is. */
const renamedSlugs = {
	banks: 'accounts',
};

export function currentSlug(slug) {
	return renamedSlugs[slug] || slug;
}

/**
 * What the bottom bar lists: each section outside a group, and in place of a group's sections, one
 * entry for the group. That entry leads to `lastVisited[group]` when it is one of the group's
 * sections, and to the group's first otherwise, and is active while any of them is.
 *
 * @returns {Array<{slug: string, label: string, icon: string, key: string, active: boolean}>}
 */
export function bottomBarEntries(sections, activeSlug, lastVisited = {}) {
	const entries = [];
	const seen = new Set();
	sections.forEach((section) => {
		if(!section.group) {
			entries.push({ ...section, key: section.slug, active: section.slug === activeSlug });
			return;
		}
		if(seen.has(section.group)) {
			return;
		}
		seen.add(section.group);
		const members = sections.filter((each) => each.group === section.group);
		const remembered = members.find((each) => each.slug === lastVisited[section.group]);
		const group = sectionGroups[section.group];
		entries.push({
			key: `group-${section.group}`,
			slug: (remembered || members[0]).slug,
			label: group.label,
			icon: group.icon,
			active: members.some((each) => each.slug === activeSlug),
		});
	});
	return entries;
}

/** The sections of `section`'s group, in order; none for a section outside a group. */
export function groupMembers(sections, section) {
	return section && section.group ? sections.filter((each) => each.group === section.group) : [];
}
